import type { NodeType } from "./api";

/**
 * Canvas-paint RGB triples mirroring the obsidian shadcn tokens in
 * `styles.css` (`--primary`, `--background`, `--foreground`,
 * `--muted-foreground`). Kept as plain numbers rather than read live from the
 * CSS custom properties: canvas 2D `fillStyle`/`strokeStyle` can't consume a
 * `var(...)`, and paint.ts recomputes several of these every frame for every
 * node, so a `getComputedStyle()` round-trip per paint is a non-starter.
 * Every canvas surface (atlas + orbits) draws from this ONE set of numbers
 * instead of copy-pasted magic rgba strings — if the obsidian theme's HSL
 * values ever move, this is the only place canvas paint needs to follow.
 */
export const CANVAS_PRIMARY = "132, 115, 222"; // --primary (250 62% 66%)
export const CANVAS_PRIMARY_BRIGHT = "169, 156, 232"; // --primary, lightened for pulses/peaks
export const CANVAS_BACKGROUND = "31, 31, 31"; // --background (0 0% 12%)
export const CANVAS_FOREGROUND = "221, 220, 217"; // --foreground (40 6% 86%)
export const CANVAS_MUTED = "157, 154, 149"; // --muted-foreground (40 4% 60%)

/** Node-type colors — single source for canvas paint, legend, and badges.
 * `document` (the flagship type) echoes the studio's violet `--primary`
 * accent; the rest lean on the fixed categorical accents from
 * tailwind.config (viridian/skyb/orchid/parch/moss) so every type stays
 * distinguishable regardless of which brand theme is active. `org` keeps its
 * own saturated signal green — a hub is deliberately louder than a category. */
export const NODE_COLOR: Record<NodeType, string> = {
  document: "#8473DE", // primary violet — matches CANVAS_PRIMARY (132, 115, 222) above
  section: "#A8A18A", // parch
  fragment: "#8C9683", // moss
  concept: "#5FAE8B", // viridian
  note: "#7FB4D8", // skyb
  ai_output: "#BC8FD9", // orchid
  org: "#4ADE80", // signal green — organizational hubs
};

export const NODE_RADIUS: Record<NodeType, number> = {
  document: 7,
  section: 3.8,
  fragment: 3.2,
  concept: 5.4,
  note: 4.4,
  ai_output: 4.4,
  org: 6.4,
};

export const NODE_LABELS: Record<NodeType, string> = {
  document: "document",
  section: "section",
  fragment: "fragment",
  concept: "concept",
  note: "note",
  ai_output: "ai output",
  org: "organizational",
};

export const EDGE_COLOR: Record<string, string> = {
  contains: "#333338",
  references: "#48485A",
  mentions: "#3D4A52",
  explains: "#4A3D66",
  derived_from: "#4A3D66",
  about: "#3D4566",
  similar_to: "#3A4A40",
  depends_on: "#5C4E33",
  extends: "#5C4E33",
  contradicts: "#5C3636",
  organizes: "#3E7D55",
  // Folder metadata. It was missing, so every co-located pair fell through to
  // the `contains` grey and the folder mesh was indistinguishable from the
  // parser's document structure — the two most numerous edge classes in a real
  // vault, painted the same colour.
  co_located: "#3A4653",
};

/** Every edge type the atlas can paint.
 *
 * This is the PALETTE's vocabulary, not the ontology's. The ontology lives on
 * the server (`graphs/ontology.py`) and is served by `GET /api/graph/ontology`;
 * a picker that needs to know what is LEGAL between two nodes asks for that,
 * because a second copy of the matrix here would drift — as this list did,
 * missing `co_located` for as long as that edge type has existed.
 * `web/app/src/lib/palette.test.ts` pins the two lists against each other. */
export const EDGE_TYPES = Object.keys(EDGE_COLOR);

export const humanizeEdgeType = (t: string): string => t.replace(/_/g, " ");

// ── folders & note categories ───────────────────────────────────────────────

/** Folder colours are TOKEN names on the server (`domain/vault.FOLDER_COLORS`),
 * never free hex: a stored hex value would outlive the theme that made it
 * legible, and would reach a style attribute straight from user input. This is
 * the one place a token becomes a colour, drawn from the accents already in
 * tailwind.config.ts and NODE_COLOR above. */
export const FOLDER_COLOR: Record<string, string> = {
  violet: "#8473DE", // --primary
  viridian: "#5FAE8B",
  sky: "#7FB4D8",
  orchid: "#BC8FD9",
  ember: "#D9705F",
  parch: "#A8A18A",
  moss: "#8C9683",
  signal: "#4ADE80",
};

/** The colour a folder is drawn in, falling back to a neutral for the many
 * folders nobody has coloured. An unknown token resolves to the fallback rather
 * than to `undefined`, so a token added server-side before the studio knows it
 * degrades quietly. */
export const folderColor = (token: string | null | undefined): string =>
  (token && FOLDER_COLOR[token]) || "hsl(var(--muted-foreground) / 0.55)";

/** Category accents. Each reuses the node type that category is made of, so the
 * homepage and the atlas agree on what "a note" or "a document" looks like. */
export const CATEGORY_COLOR: Record<string, string> = {
  markdown: NODE_COLOR.note,        // skyb
  ingested: NODE_COLOR.document,    // primary violet
  handwritten: NODE_COLOR.concept,  // viridian
  website: NODE_COLOR.ai_output,    // orchid
};
